import { isEmpty } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useContent } from 'src/contexts/ContentContext';
import { ContentId } from 'src/utils/constants/contentId';
import { GroupingType } from 'src/WebApiController';
import { ListingGroup, ListingGroupItemInput } from 'src/WebApiController';

import { MergeListingGroupInputListFields } from './components/groupingTypes';
import { flattenListingGroup } from './components/groupingUtils';
import { GroupListingsModalContent } from './GroupListings';

export const AddToGroupListingsModal = ({
  selectedListingIds,
  listingGroupId,
  viagVirtId,
  newListingGroupName,
  inheritListingGroupSettings,
  maintainPriceStagger,
  onGroupCreated,
}: {
  selectedListingIds: number[];
  listingGroupId?: string | undefined;
  viagVirtId: string;
  newListingGroupName?: string;
  inheritListingGroupSettings?: boolean;
  maintainPriceStagger?: boolean;
  onGroupCreated?: (groupId: string) => void;
}) => {
  const { eventsTransformed } = useCatalogDataContext();

  const eventData = useMemo(() => {
    return eventsTransformed?.find((e) => e.event.virtId === viagVirtId);
  }, [eventsTransformed, viagVirtId]);

  const listingsToAdd = useMemo(() => {
    return eventData?.entities?.listings
      ?.flatMap(flattenListingGroup)
      .filter((l) => selectedListingIds.includes(l.id));
  }, [eventData, selectedListingIds]);

  const listingGroup = useMemo(() => {
    if (!isEmpty(newListingGroupName)) {
      // !important don't assign ltGrpId, as this is the indicator of when this is a new listing group
      // But we need ths isLtGrp, which is used to flatten the listings from the group
      return {
        groupName: newListingGroupName,
        isLtGrp: true,
        desiredActiveListings: null,
        minActiveListings: null,
        maxActiveListings: null,
        targetPercentage: null,
        deprioritizedQuantities: null,
        marketplaceSettings: null,
        absoluteRankPremium: null,
        relativeRankPremium: null,
      } as ListingGroup;
    }
    return eventData?.entities?.listings?.find(
      (l) => l.isLtGrp && l.ltGrpId === listingGroupId
    ) as ListingGroup;
  }, [eventData?.entities?.listings, listingGroupId, newListingGroupName]);

  const event = eventData?.event;

  const listings = useMemo(() => {
    return [...flattenListingGroup(listingGroup), ...(listingsToAdd ?? [])];
  }, [listingGroup, listingsToAdd]);

  const newGroupName = useContent(ContentId.NewGroup);
  const methods = useForm<MergeListingGroupInputListFields>({
    defaultValues: {
      mergeListingGroupInputs: [
        {
          listingGroupId: listingGroup?.ltGrpId ?? listingGroupId,
          name: listingGroup?.groupName ?? `${newGroupName} 1`,
          listingGroupItems: listings.map(
            (l, idx) =>
              ({
                listingId: l.id,
                priority: l.ltGrpPrior ?? idx + 1,
                groupId: l.ltGrpId,
                undercutSetting: l.ltGrpUndercut,
              }) as ListingGroupItemInput
          ),
          viagogoEventId: event?.viagId,
          viagogoMappingId: event?.mpId,
          desiredActiveListings: listingGroup?.desiredActiveListings ?? 1,
          minActiveListings: listingGroup?.minActiveListings,
          maxActiveListings: listingGroup?.maxActiveListings,
          targetPercentage: listingGroup?.targetPercentage,
          deprioritizedQuantities: listingGroup?.deprioritizedQuantities ?? [],
          marketplaceSettings: listingGroup?.marketplaceSettings,
          inheritListingGroupSettings,
          maintainPriceStagger,
          groupUndercutSetting: {
            undAbsAmt: listingGroup?.absoluteRankPremium,
            undRelAmt: listingGroup?.relativeRankPremium,
            actRankUndAbsAmt: null,
            actRankUndRelAmt: null,
          },
        },
      ],
      templateSettings: {
        groupBy: {
          groupingType: GroupingType.Custom,
        },
        desiredActiveListings: 0,
        deprioritizedQuantities: [],
      },
      eventSettings: [],
    },
  });

  const { reset } = methods;

  useEffect(() => {
    reset({
      mergeListingGroupInputs: [
        {
          name: listingGroup?.groupName ?? `${newGroupName} 1`,
          listingGroupId: listingGroup?.ltGrpId ?? listingGroupId,
          listingGroupItems: listings.map(
            (l, idx) =>
              ({
                listingId: l.id,
                priority: l.ltGrpPrior ?? idx + 1,
                groupId: l.ltGrpId,
                undercutSetting: l.ltGrpUndercut,
              }) as ListingGroupItemInput
          ),
          viagogoEventId: event?.viagId,
          viagogoMappingId: event?.mpId,
          desiredActiveListings: listingGroup?.desiredActiveListings ?? 1,
          minActiveListings: listingGroup?.minActiveListings,
          maxActiveListings: listingGroup?.maxActiveListings,
          targetPercentage: listingGroup?.targetPercentage,
          deprioritizedQuantities: listingGroup?.deprioritizedQuantities ?? [],
          marketplaceSettings: listingGroup?.marketplaceSettings,
          inheritListingGroupSettings,
          maintainPriceStagger,
          groupUndercutSetting: {
            undAbsAmt: listingGroup?.absoluteRankPremium,
            undRelAmt: listingGroup?.relativeRankPremium,
            actRankUndAbsAmt: null,
            actRankUndRelAmt: null,
          },
        },
      ],
      templateSettings: {
        groupBy: {
          groupingType: GroupingType.Custom,
        },
        desiredActiveListings: 0,
        deprioritizedQuantities: [],
      },
      eventSettings: [],
    });
  }, [
    listings,
    listingGroup,
    event,
    newGroupName,
    reset,
    inheritListingGroupSettings,
    maintainPriceStagger,
    listingGroupId,
  ]);
  return (
    <FormProvider {...methods}>
      <GroupListingsModalContent
        {...methods}
        events={event ? [event] : undefined}
        listingGroup={listingGroup}
        onGroupCreated={onGroupCreated}
      />
    </FormProvider>
  );
};
